import { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import {
  type BulkItem,
  type BulkItemResult,
  settleEach,
} from 'src/utils/bulkResults';
import {
  BuildApplicationInput,
  BuildApplicationResult,
  CreateApplicationDto,
  GenerateHybridSheetInput,
  ParseApplicationSheetResult,
  SearchApplicationResultDto,
  type ApplicationHierarchyResult,
} from 'switch-shared/modules/crud/application/application.dto';
import { Application } from 'switch-shared/modules/crud/application/application.entity';
import type {
  BulkRenameItem,
  BulkRenameResult,
} from 'switch-shared/modules/crud/base/base.dto';
import { UpdateDto } from 'switch-shared/modules/crud/base/base.service';

import { baseAPI, setToast } from './baseAPI.service';
import { API_METHODS, ENDPOINT_URLS } from './config/apiTags';

export const applicationAPI = baseAPI
  .enhanceEndpoints({
    addTagTypes: [
      ENDPOINT_URLS.APPLICATION,
      ENDPOINT_URLS.BULK_APPLICATION_SETUP,
    ],
  })
  .injectEndpoints({
    overrideExisting: true,
    endpoints: (builder) => ({
      getApplicationById: builder.query<
        Application,
        { id: string; timestamp: number }
      >({
        query: (params) => `${ENDPOINT_URLS.APPLICATION}/${params.id}`,
        // Keyed on _id rather than id: the single-fetch response omits the
        // derived serialized primary key, and mutations invalidate using _id.
        providesTags: (res) =>
          res ? [{ type: ENDPOINT_URLS.APPLICATION, id: res._id }] : [],
      }),

      getAllApplications: builder.query<SearchApplicationResultDto, any>({
        query: (params) => {
          return {
            url: `${ENDPOINT_URLS.APPLICATION}`,
            params,
          };
        },
        providesTags: (res, _err, _) =>
          res?.data.map((gen) => ({
            type: ENDPOINT_URLS.APPLICATION,
            id: gen._id,
          })) ?? [],
      }),

      createApplication: builder.mutation<Application, CreateApplicationDto>({
        query: (data) => ({
          url: `${ENDPOINT_URLS.APPLICATION}`,
          method: API_METHODS.POST,
          body: data,
        }),
        invalidatesTags: [ENDPOINT_URLS.APPLICATION],
      }),

      updateApplication: builder.mutation<
        Application,
        { id: string; data: UpdateDto<Application> }
      >({
        query: ({ id, data }) => ({
          url: `${ENDPOINT_URLS.APPLICATION}/${id}`,
          method: API_METHODS.PATCH,
          body: data,
        }),
        invalidatesTags: (_res, _err, { id }) => [
          {
            type: ENDPOINT_URLS.APPLICATION,
            id,
          },
        ],
      }),

      bulkRenameApplications: builder.mutation<
        BulkRenameResult[],
        BulkRenameItem[]
      >({
        query: (items) => ({
          url: `${ENDPOINT_URLS.APPLICATION}/bulk-rename`,
          method: API_METHODS.PATCH,
          body: { items },
        }),
        invalidatesTags: (_res, _err, items) =>
          items.map(({ id }) => ({ type: ENDPOINT_URLS.APPLICATION, id })),
      }),

      /** Deletes each item with its own request, reporting per-item results. */
      bulkDeleteApplications: builder.mutation<BulkItemResult[], BulkItem[]>({
        queryFn: async (items, _api, _extra, baseQuery) => ({
          data: await settleEach(items, ({ id }) =>
            baseQuery({
              url: `${ENDPOINT_URLS.APPLICATION}/${id}`,
              method: API_METHODS.DELETE,
            })
          ),
        }),
        invalidatesTags: (_res, _err, items) =>
          items.map(({ id }) => ({ type: ENDPOINT_URLS.APPLICATION, id })),
      }),

      deleteApplication: builder.mutation<{ success: boolean }, string>({
        query: (id) => ({
          url: `${ENDPOINT_URLS.APPLICATION}/${id}`,
          method: API_METHODS.DELETE,
        }),
        invalidatesTags: (_res, _err, id) => [
          {
            type: ENDPOINT_URLS.APPLICATION,
            id,
          },
        ],
      }),
      generateApplicationSheet: builder.mutation<
        void,
        {
          applicationId?: string;
          name?: string;
          templateConfig?: string;
        }
      >({
        queryFn: async (params, _queryApi, _extraOptions, baseQuery) => {
          const queryParams: Record<string, string> = {};
          if (params.applicationId)
            queryParams.applicationId = params.applicationId;
          if (params.templateConfig)
            queryParams.templateConfig = params.templateConfig;

          const result = await baseQuery({
            url: `${ENDPOINT_URLS.APPLICATION}/generateSheet`,
            params:
              Object.keys(queryParams).length > 0 ? queryParams : undefined,
            responseHandler: (response) => response.blob(),
          });

          if (result.error) {
            return { error: result.error as FetchBaseQueryError };
          }

          const blob = result.data as Blob;
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          const filename = params.name
            ? `${params.name} - Application Sheet.xlsx`
            : 'application-sheet.xlsx';
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);

          return { data: undefined };
        },
      }),

      parseApplicationSheet: builder.mutation<
        ParseApplicationSheetResult,
        {
          file: File;
          applicationId?: string;
          applicationName?: string;
          applicationGroupId?: string;
        }
      >({
        queryFn: async (params, _queryApi, _extraOptions, baseQuery) => {
          const { file, ...queryParams } = params;
          const formData = new FormData();
          formData.append('file', file);

          const result = await baseQuery({
            url: `${ENDPOINT_URLS.APPLICATION}/parseSheet`,
            method: 'POST',
            params: queryParams,
            body: formData,
          });

          if (result.error) {
            return { error: result.error as FetchBaseQueryError };
          }

          return { data: result.data as ParseApplicationSheetResult };
        },
        invalidatesTags: [ENDPOINT_URLS.APPLICATION],
      }),

      createApplicationFromWizard: builder.mutation<
        BuildApplicationResult,
        BuildApplicationInput
      >({
        query: (data) => ({
          url: `${ENDPOINT_URLS.APPLICATION}/build`,
          method: API_METHODS.POST,
          body: data,
        }),
        invalidatesTags: [ENDPOINT_URLS.APPLICATION],
      }),

      getApplicationHierarchy: builder.query<
        ApplicationHierarchyResult,
        { applicationId: string; utilityTypes?: string[] }
      >({
        query: ({ applicationId, utilityTypes }) => {
          const base = `${ENDPOINT_URLS.APPLICATION}/hierarchy/${applicationId}`;
          if (utilityTypes && utilityTypes.length > 0) {
            return `${base}?utilityTypes=${utilityTypes.join(',')}`;
          }
          return base;
        },
      }),

      exportApplicationStructure: builder.mutation<
        void,
        { applicationId: string; applicationName: string }
      >({
        queryFn: async (params, _queryApi, _extraOptions, baseQuery) => {
          const result = await baseQuery({
            url: `${ENDPOINT_URLS.APPLICATION}/hierarchy/${params.applicationId}/export`,
            responseHandler: (response) => response.blob(),
          });

          if (result.error) {
            return { error: result.error as FetchBaseQueryError };
          }

          const blob = result.data as Blob;
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${params.applicationName || 'application'}-export.xlsx`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);

          return { data: undefined };
        },
      }),

      generateHybridApplicationSheet: builder.mutation<
        void,
        GenerateHybridSheetInput
      >({
        queryFn: async (params, _queryApi, _extraOptions, baseQuery) => {
          const result = await baseQuery({
            url: `${ENDPOINT_URLS.APPLICATION}/generateHybridSheet`,
            method: API_METHODS.POST,
            body: params,
            responseHandler: (response) => response.blob(),
          });

          if (result.error) {
            return { error: result.error as FetchBaseQueryError };
          }

          const blob = result.data as Blob;
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${params.name || 'application'}-setup.xlsx`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);

          return { data: undefined };
        },
      }),
    }),
  });

export const {
  useGetApplicationByIdQuery,
  useGetAllApplicationsQuery,
  useLazyGetAllApplicationsQuery,
  useCreateApplicationMutation,
  useUpdateApplicationMutation,
  useBulkRenameApplicationsMutation,
  useDeleteApplicationMutation,
  useBulkDeleteApplicationsMutation,
  useGenerateApplicationSheetMutation,
  useParseApplicationSheetMutation,
  useCreateApplicationFromWizardMutation,
  useGetApplicationHierarchyQuery,
  useExportApplicationStructureMutation,
  useGenerateHybridApplicationSheetMutation,
} = applicationAPI;
export default applicationAPI;

setToast('bulkRenameApplications', {
  bulkResults: { noun: 'application', verb: 'rename' },
});
setToast('bulkDeleteApplications', {
  bulkResults: { noun: 'application', verb: 'delete' },
});
